import React, { createContext, useContext, useState, useEffect } from 'react';
import { Auth0ContextInterface } from '../lib/keycloak-auth0-shim';
import authService, { User } from '../services/auth';
import api from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: any) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  authMode: 'auth0' | 'custom';
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
  auth0?: Auth0ContextInterface | null;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, auth0 }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authSynced, setAuthSynced] = useState(false);
  
  const authMode = auth0 ? 'auth0' : 'custom';

  useEffect(() => {
    const initAuth = async () => {
      try {
        if (authMode === 'auth0' && auth0) {
          // Auth0 mode
          if (auth0.isAuthenticated && auth0.user && !authSynced) {
            const token = await auth0.getAccessTokenSilently();
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            
            // Sync with backend
            const response = await api.post('/auth/sync', {
              auth0Id: auth0.user.sub,
              email: auth0.user.email,
              name: auth0.user.name,
              picture: auth0.user.picture
            });
            
            // Store token and user data
            if (response.data.token) {
              localStorage.setItem('token', response.data.token);
              // Update axios default header with new token
              api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
            }
            setUser(response.data.user || response.data);
            setAuthSynced(true);
          }
        } else {
          // Custom auth mode
          if (authService.isAuthenticated()) {
            const userData = await authService.getMe();
            setUser(userData);
          }
        }
      } catch (error) {
        console.error('Auth init error:', error);
        if (authMode === 'custom') {
          authService.logout();
        }
      } finally {
        setLoading(false);
      }
    };

    // Wait for Auth0 to finish loading if in Auth0 mode
    if (authMode === 'auth0' && auth0?.isLoading) {
      return;
    }

    initAuth();
  }, [authMode, auth0?.isAuthenticated, auth0?.user?.sub, auth0?.isLoading, authSynced]);

  const login = async (email: string, password: string) => {
    if (authMode === 'auth0' && auth0) {
      // In Auth0 mode, redirect to Auth0 login
      await auth0.loginWithRedirect({
        appState: { returnTo: '/dashboard' }
      });
    } else {
      // Custom auth mode
      const userData = await authService.login({ email, password });
      setUser(userData);
    }
  };

  const register = async (data: any) => {
    if (authMode === 'auth0' && auth0) {
      // In Auth0 mode, redirect to Auth0 signup
      await auth0.loginWithRedirect({
        appState: { returnTo: '/onboarding' },
        authorizationParams: {
          screen_hint: 'signup'
        }
      });
    } else {
      // Custom auth mode
      const userData = await authService.register(data);
      setUser(userData);
    }
  };

  const logout = () => {
    if (authMode === 'auth0' && auth0) {
      auth0.logout({ logoutParams: { returnTo: window.location.origin } });
    } else {
      authService.logout();
    }
    setUser(null);
    setAuthSynced(false);
  };

  const value = {
    user,
    loading: loading || (authMode === 'auth0' && auth0?.isLoading) || false,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    authMode: authMode as 'auth0' | 'custom',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};